import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import "./ImageEditor.scss";
import { fileUploads } from "../../../services/fileUploadService";
import ImagePreview from "./ImagePreview";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import Cropper from "react-easy-crop";

const ImageEditorModal = ({
  imgSrc: initialImgSrc,
  onClose,
  onSave,
  onDelete,
  originalFile,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentImgSrc, setCurrentImgSrc] = useState(initialImgSrc);

  const initialCrop = useRef({ x: 0, y: 0 });
  const initialZoom = useRef(1);
  const initialRotation = useRef(0);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  async function getCroppedImgHelper(imageSrc, pixelCrop, rotation = 0) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const rotRad = (rotation * Math.PI) / 180;
    const width = image.width;
    const height = image.height;

    const sin = Math.abs(Math.sin(rotRad));
    const cos = Math.abs(Math.cos(rotRad));
    const newWidth = cos * width + sin * height;
    const newHeight = sin * width + cos * height;

    canvas.width = newWidth;
    canvas.height = newHeight;

    ctx.translate(newWidth / 2, newHeight / 2);
    ctx.rotate(rotRad);
    ctx.translate(-width / 2, -height / 2);

    ctx.drawImage(image, 0, 0);

    const croppedCanvas = document.createElement("canvas");
    const croppedCtx = croppedCanvas.getContext("2d");

    croppedCanvas.width = pixelCrop.width;
    croppedCanvas.height = pixelCrop.height;

    croppedCtx.drawImage(
      canvas,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      croppedCanvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        0.95
      );
    });
  }

  useEffect(() => {
    setCurrentImgSrc(initialImgSrc);
  }, [initialImgSrc]);

  useEffect(() => {
    const isCropChanged =
      crop.x !== initialCrop.current.x || crop.y !== initialCrop.current.y;
    const isZoomChanged = zoom !== initialZoom.current;
    const isRotationChanged = rotation !== initialRotation.current;
    const isImageChanged = currentImgSrc !== initialImgSrc;

    setHasChanges(
      isCropChanged || isZoomChanged || isRotationChanged || isImageChanged
    );
  }, [crop.x, crop.y, zoom, rotation, currentImgSrc, initialImgSrc]);

  const handleChangePhoto = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const newImageUrl = URL.createObjectURL(file);
        setCurrentImgSrc(newImageUrl);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setCroppedAreaPixels(null);
      }
    };
    input.click();
  };

  const getCroppedImg = async () => {
    if (!hasChanges) return originalFile;
    if (!croppedAreaPixels) return null;

    try {
      const blob = await getCroppedImgHelper(
        currentImgSrc,
        croppedAreaPixels,
        rotation
      );
      return blob;
    } catch (error) {
      console.error("Error cropping image:", error);
      return null;
    }
  };

  const handleApply = async () => {
    try {
      const result = await getCroppedImg();
      onSave(result);
      onClose();
    } catch (error) {
      console.error("Error applying changes:", error);
    }
  };

  return (
    <Modal open={!!!!currentImgSrc} onClose={onClose}>
      <Box className="modal-container">
        <div className="photo-header">
          <span className="photo-title">Background photo</span>
          <div className="close-icon" onClick={onClose}>
            <CloseIcon />
          </div>
        </div>

        <div className="editor-container">
          <div className="crop-container">
            <Cropper
              image={currentImgSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={4 / 1.5}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={(_, croppedAreaPixels) => {
                setCroppedAreaPixels(croppedAreaPixels);
              }}
              classes={{
                containerClassName: "crop-container",
                mediaClassName: "crop-media",
                cropAreaClassName: "fixed-crop-area",
              }}
            />
          </div>

          <div className="image-controls">
            <div className="controls-wrapper">
              <div className="zoom-controls">
                <label className="zoom-label">Zoom</label>
                <div className="zoom-slider">
                  <button
                    className="zoom-btn"
                    onClick={() => setZoom((prev) => Math.max(prev - 0.1, 1))}
                  >
                    -
                  </button>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                  />
                  <button
                    className="zoom-btn"
                    onClick={() => setZoom((prev) => Math.min(prev + 0.1, 3))}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="rotate-controls">
                <button
                  onClick={() => setRotation((prev) => (prev - 90) % 360)}
                >
                  ↺
                </button>
                <button
                  onClick={() => setRotation((prev) => (prev + 90) % 360)}
                >
                  ↻
                </button>
              </div>
            </div>
          </div>
          <div className="action-controls">
            <div className="left-section">
              <button className="delete-btn" onClick={onDelete}>
                Delete photo
              </button>
            </div>
            <div className="right-section">
              <button className="change-btn" onClick={handleChangePhoto}>
                Change photo
              </button>
              <button className="apply-btn" onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

const ImageUploader = ({
  handleFileChange,
  acceptedFileType = ["image/jpeg", "image/png", "image/jpg"],
  key,
  id,
  imageUrl,
}) => {
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isLoader, setIsLoader] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const MAX_FILE_SIZE = 262144000;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => handleDrop(acceptedFiles),
    accept: acceptedFileType,
  });

  const handleDrop = useCallback(
    (acceptedFiles) => {
      setErrorMsg("");
      if (uploadedFiles.length >= 1) {
        setErrorMsg("You can upload only 1 image.");
        return;
      }
      if (acceptedFiles.length > 1) {
        setErrorMsg("You can upload only 1 image.");
        return;
      }
      if (acceptedFiles[0]?.size > MAX_FILE_SIZE) {
        setErrorMsg("The uploaded file is larger than 250 MB.");
        return;
      }
      if (!acceptedFiles[0]?.type.startsWith("image/")) {
        setErrorMsg("Only image files are allowed.");
        return;
      }
      const file = acceptedFiles[0];
      setFileToUpload(file);
      setEditingFile(URL.createObjectURL(file));
      setEditModalOpen(true);
    },
    [uploadedFiles]
  );

  const handleEditedImage = async (result) => {
    try {
      let fileToProcess;

      if (result instanceof File) {
        fileToProcess = result;
      } else {
        const originalName = fileToUpload.name.split(".")[0];
        const sanitizedBaseName = originalName.replace(/\s+/g, "_");
        const originalExtension = fileToUpload.name
          .split(".")
          .pop()
          .toLowerCase();
        const finalExtension =
          originalExtension === "jpeg" ? "jpg" : originalExtension;

        fileToProcess = new File(
          [result],
          `${sanitizedBaseName}_edited_${Date.now()}.${finalExtension}`,
          {
            type: `image/${finalExtension === "png" ? "png" : "jpeg"}`,
            lastModified: Date.now(),
          }
        );
      }

      // Check size again
      if (fileToProcess.size > MAX_FILE_SIZE) {
        setErrorMsg("The uploaded file is larger than 250 MB.");
        return;
      }
      await handleFileProcessing([fileToProcess]);
    } catch (error) {
      console.error("Error processing image", error);
    } finally {
      setEditModalOpen(false);
      if (editingFile) URL.revokeObjectURL(editingFile);
    }
  };

  // Upload logic
  const handleFileProcessing = async (filesArr) => {
    try {
      setIsLoader(true);
      const uploadResponses = await Promise.all(
        filesArr.map(async (file) => await fileUploads(file))
      );
      const newFiles = uploadResponses.flat();
      setFiles((prev) => [...prev, ...newFiles]);
      const merged = [...uploadedFiles, ...newFiles];
      setUploadedFiles(merged);
      const filesPath = merged.map((x) => x.fileUrl).join("|");
      const fileDataId = merged.map((x) => x.id);
      handleFileChange(filesPath, fileDataId);
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setIsLoader(false);
    }
  };

  // Remove an uploaded image
  const deleteImageURL = (url, index) => {
    const updatedFiles = files.filter((f) => f.fileUrl !== url);
    setFiles(updatedFiles);
    const updatedUploaded = uploadedFiles.filter((f) => f.fileUrl !== url);
    setUploadedFiles(updatedUploaded);
    const filesPath = updatedUploaded.map((f) => f.fileUrl).join("|");
    const fileDataId = updatedUploaded.map((f) => f.id);
    handleFileChange(filesPath, fileDataId);
  };

  return (
    <div className={isLoader ? "image-uploader-container pointerNone" : "image-uploader-container"} key={key} id={id}>
      <div className="upload-container">
        <div
          {...getRootProps()}
          className={`dropzone-area ${isDragActive ? "active" : ""}`}
        >
          <input {...getInputProps()} />
          <CloudUploadOutlinedIcon className="upload-icon" />
          <p className="upload-text">
            <span className="upload-text-drop-text">Drop Image here,</span>
            <span className="browse-text">or Browse</span>
          </p>
          <p className="upload-note">You can upload maximum 250 MB files.</p>
        </div>
      </div>

      {errorMsg && <p className="errorMsg">{errorMsg}</p>}
      <ImagePreview
        files={uploadedFiles}
        imageUrl={uploadedFiles.map((f) => f.fileUrl).join("|") || imageUrl}
        deleteImageURL={deleteImageURL}
        isLoader={isLoader}
      />
      {editModalOpen && (
        <ImageEditorModal
          imgSrc={editingFile}
          originalFile={fileToUpload}
          onSave={handleEditedImage}
          onClose={() => {
            setEditModalOpen(false);
            if (editingFile) URL.revokeObjectURL(editingFile);
          }}
          onDelete={() => {
            setEditModalOpen(false);
            if (editingFile) URL.revokeObjectURL(editingFile);
          }}
        />
      )}
    </div>
  );
};

export default ImageUploader;
